import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { publicAPI } from './lib/api';

import Growth from './pages/growth/Growth';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Inventory from './pages/Inventory';
import Customers from './pages/Customers';
import Suppliers from './pages/Suppliers';
import SalesOrders from './pages/SalesOrders';
import CODSettlements from './pages/CODSettlements';
import Invoices from './pages/Invoices';
import PurchaseOrders from './pages/PurchaseOrders';
import Payments from './pages/Payments';
import Accounting from './pages/Accounting';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import GRN from './pages/GRN';
import Collections from './pages/Collections';
import Categories from './pages/Categories';
import Brands from './pages/Brands';
import SizeGuides from './pages/SizeGuides';
import Coupons from './pages/Coupons';

// Advanced Finance Pages
import ChartOfAccounts from './pages/ChartOfAccounts';
import GeneralLedger from './pages/GeneralLedger';
import FinancialReports from './pages/FinancialReports';
import FinanceControlCentre from './pages/FinanceControlCentre';

// Admin Pages
import SystemAdmin from './pages/SystemAdmin';
import ERPValidation from './pages/ERPValidation';
import AuditLogs from './pages/AuditLogs';
import UserManagement from './pages/UserManagement';
import PackagingRules from './pages/PackagingRules';

// Documentation
import Documentation from './pages/Documentation';

// Simple Finance Pages
import Investors from './pages/Investors';
import QuickTransactions from './pages/QuickTransactions';
import BankAccounts from './pages/BankAccounts';
import PayHereSettlements from './pages/PayHereSettlements';

// Manufacturing Pages
import RawMaterials from './pages/RawMaterials';
import BillOfMaterials from './pages/BillOfMaterials';
import WorkOrders from './pages/WorkOrders';
import ManufacturingDashboard from './pages/ManufacturingDashboard';

// RM Procurement Pages
import RMSuppliers from './pages/RMSuppliers';
import RMPurchaseOrders from './pages/RMPurchaseOrders';
import RMGRN from './pages/RMGRN';
import RMGRNReturns from './pages/RMGRNReturns';

// Payroll Pages
import Departments from './pages/payroll/Departments';
import Designations from './pages/payroll/Designations';
import Employees from './pages/payroll/Employees';
import SalaryStructure from './pages/payroll/SalaryStructure';
import LeaveManagement from './pages/payroll/LeaveManagement';
import Advances from './pages/payroll/Advances';
import Payroll from './pages/payroll/Payroll';
import PayrollReports from './pages/payroll/PayrollReports';
import TaskAssignments from './pages/payroll/TaskAssignments';
import AttendanceTracking from './pages/payroll/AttendanceTracking';

// Employee Portal Pages
import MyDashboard from './pages/MyDashboard';
import MyTasks from './pages/MyTasks';
import TaskCategories from './pages/TaskCategories';
import MyProfile from './pages/MyProfile';


// Fixed Asset Management
import AssetDashboard from './pages/assets/AssetDashboard';
import AssetRegister from './pages/assets/AssetRegister';
import AssetDetails from './pages/assets/AssetDetails';
import AssetCategories from './pages/assets/AssetCategories';
import AssetAcquisitions from './pages/assets/AssetAcquisitions';
import AssetCapitalizations from './pages/assets/AssetCapitalizations';
import DepreciationRuns from './pages/assets/DepreciationRuns';
import AssetTransfers from './pages/assets/AssetTransfers';
import AssetAssignments from './pages/assets/AssetAssignments';
import AssetMaintenance from './pages/assets/AssetMaintenance';
import AssetImpairments from './pages/assets/AssetImpairments';
import AssetRevaluations from './pages/assets/AssetRevaluations';
import AssetDisposals from './pages/assets/AssetDisposals';
import AssetStocktakes from './pages/assets/AssetStocktakes';
import AssetReports from './pages/assets/AssetReports';
import AssetSettings from './pages/assets/AssetSettings';
import AssetMasterData from './pages/assets/AssetMasterData';
import AssetCWIP from './pages/assets/AssetCWIP';
import { hasAssetModule } from './components/assets/AssetPermissionGuard';

// Layout
import Layout from './components/Layout';

const faviconUrl = (asset) => {
  const url = asset?.url;
  if (!url) return null;
  const transformed = url.includes('/image/upload/')
    ? url.replace('/image/upload/', '/image/upload/f_png,q_auto,c_fit,w_128,h_128,b_transparent/')
    : url;
  const version = asset.version || asset.public_id;
  if (!version) return transformed;
  return `${transformed}${transformed.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}`;
};

const applyBranding = (branding = {}) => {
  if (branding.name) document.title = `${branding.name} ERP`;
  const href = faviconUrl(branding.erp_browser_logo || branding.logo) || '/favicon.ico';
  const relationships = ['icon', 'shortcut icon', 'apple-touch-icon'];
  relationships.forEach((rel) => {
    const links = Array.from(document.head.querySelectorAll(`link[rel='${rel}']`));
    const targets = links.length ? links : [document.createElement('link')];
    targets.forEach((link) => {
      link.rel = rel;
      link.href = href;
      link.type = 'image/png';
      if (!link.parentNode) document.head.appendChild(link);
    });
  });
};

const BrandingManager = () => {
  useEffect(() => {
    let active = true;
    const loadBranding = () => publicAPI.getStorefrontStatus()
      .then((response) => {
        if (active) applyBranding(response.data.branding);
      })
      .catch((error) => console.warn('Could not load company branding', error));

    loadBranding();
    window.addEventListener('company-branding-updated', loadBranding);
    window.addEventListener('storefront-branding-updated', loadBranding);
    return () => {
      active = false;
      window.removeEventListener('company-branding-updated', loadBranding);
      window.removeEventListener('storefront-branding-updated', loadBranding);
    };
  }, []);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

const ModuleProtectedRoute = ({ module, children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner"></div></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!hasAssetModule(user, module)) {
    return <Layout><div className="mx-auto max-w-2xl rounded-xl border border-red-200 bg-red-50 p-8 text-center"><h1 className="text-2xl font-black text-red-900">Access denied</h1><p className="mt-2 text-sm text-red-700">Your role does not have access to this Fixed Asset module.</p></div></Layout>;
  }
  return <Layout>{children}</Layout>;
};

// Public Route (redirect if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
      <Route path="/categories" element={<ProtectedRoute><Categories /></ProtectedRoute>} />
      <Route path="/brands" element={<ProtectedRoute><Brands /></ProtectedRoute>} />
      <Route path="/size-guides" element={<ProtectedRoute><SizeGuides /></ProtectedRoute>} />
      <Route path="/coupons" element={<ProtectedRoute><Coupons /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/suppliers" element={<ProtectedRoute><Suppliers /></ProtectedRoute>} />
      <Route path="/sales-orders" element={<ProtectedRoute><SalesOrders /></ProtectedRoute>} />
      <Route path="/cod-settlements" element={<ProtectedRoute><CODSettlements /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/purchase-orders" element={<ProtectedRoute><PurchaseOrders /></ProtectedRoute>} />
      <Route path="/grn" element={<ProtectedRoute><GRN /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
      <Route path="/accounting" element={<ProtectedRoute><Accounting /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/collections" element={<ProtectedRoute><Collections /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />


      {/* Fixed Asset Management — separate from product inventory and storefront */}
      <Route path="/fixed-assets" element={<ModuleProtectedRoute module="fixed-assets"><AssetDashboard /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/register" element={<ModuleProtectedRoute module="fixed-assets"><AssetRegister /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/categories" element={<ModuleProtectedRoute module="asset-settings"><AssetCategories /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/master-data" element={<ModuleProtectedRoute module="asset-settings"><AssetMasterData /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/cwip" element={<ModuleProtectedRoute module="asset-acquisitions"><AssetCWIP /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/acquisitions" element={<ModuleProtectedRoute module="asset-acquisitions"><AssetAcquisitions /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/capitalizations" element={<ModuleProtectedRoute module="asset-depreciation"><AssetCapitalizations /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/depreciation" element={<ModuleProtectedRoute module="asset-depreciation"><DepreciationRuns /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/transfers" element={<ModuleProtectedRoute module="asset-operations"><AssetTransfers /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/assignments" element={<ModuleProtectedRoute module="asset-operations"><AssetAssignments /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/maintenance" element={<ModuleProtectedRoute module="asset-operations"><AssetMaintenance /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/impairments" element={<ModuleProtectedRoute module="asset-depreciation"><AssetImpairments /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/revaluations" element={<ModuleProtectedRoute module="asset-depreciation"><AssetRevaluations /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/disposals" element={<ModuleProtectedRoute module="asset-depreciation"><AssetDisposals /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/stocktakes" element={<ModuleProtectedRoute module="asset-operations"><AssetStocktakes /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/reports" element={<ModuleProtectedRoute module="asset-reports"><AssetReports /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/settings" element={<ModuleProtectedRoute module="asset-settings"><AssetSettings /></ModuleProtectedRoute>} />
      <Route path="/fixed-assets/:id" element={<ModuleProtectedRoute module="fixed-assets"><AssetDetails /></ModuleProtectedRoute>} />

      {/* Advanced Finance Routes */}
      <Route path="/chart-of-accounts" element={<ProtectedRoute><ChartOfAccounts /></ProtectedRoute>} />
      <Route path="/general-ledger" element={<ProtectedRoute><GeneralLedger /></ProtectedRoute>} />
      <Route path="/financial-reports" element={<ProtectedRoute><FinancialReports /></ProtectedRoute>} />
      <Route path="/finance-control-centre" element={<ProtectedRoute><FinanceControlCentre /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/system-admin" element={<ProtectedRoute><SystemAdmin /></ProtectedRoute>} />
      <Route path="/erp-validation" element={<ProtectedRoute><ERPValidation /></ProtectedRoute>} />

      {/* Simple Finance Routes */}
      <Route path="/investors" element={<ProtectedRoute><Investors /></ProtectedRoute>} />
      <Route path="/quick-transactions" element={<ProtectedRoute><QuickTransactions /></ProtectedRoute>} />
      <Route path="/bank-accounts" element={<ProtectedRoute><BankAccounts /></ProtectedRoute>} />
      <Route path="/payhere-settlements" element={<ProtectedRoute><PayHereSettlements /></ProtectedRoute>} />

      {/* Manufacturing Routes */}
      <Route path="/manufacturing" element={<ProtectedRoute><ManufacturingDashboard /></ProtectedRoute>} />
      <Route path="/raw-materials" element={<ProtectedRoute><RawMaterials /></ProtectedRoute>} />
      <Route path="/bom" element={<ProtectedRoute><BillOfMaterials /></ProtectedRoute>} />
      <Route path="/work-orders" element={<ProtectedRoute><WorkOrders /></ProtectedRoute>} />

      {/* RM Procurement Routes */}
      <Route path="/rm-suppliers" element={<ProtectedRoute><RMSuppliers /></ProtectedRoute>} />
      <Route path="/rm-purchase-orders" element={<ProtectedRoute><RMPurchaseOrders /></ProtectedRoute>} />
      <Route path="/rm-grn" element={<ProtectedRoute><RMGRN /></ProtectedRoute>} />
      <Route path="/rm-grn-returns" element={<ProtectedRoute><RMGRNReturns /></ProtectedRoute>} />

      {/* Payroll Routes */}
      <Route path="/departments" element={<ProtectedRoute><Departments /></ProtectedRoute>} />
      <Route path="/designations" element={<ProtectedRoute><Designations /></ProtectedRoute>} />
      <Route path="/employees" element={<ProtectedRoute><Employees /></ProtectedRoute>} />
      <Route path="/salary-structure" element={<ProtectedRoute><SalaryStructure /></ProtectedRoute>} />
      <Route path="/leave-management" element={<ProtectedRoute><LeaveManagement /></ProtectedRoute>} />
      <Route path="/advances" element={<ProtectedRoute><Advances /></ProtectedRoute>} />
      <Route path="/payroll" element={<ProtectedRoute><Payroll /></ProtectedRoute>} />
      <Route path="/payroll-reports" element={<ProtectedRoute><PayrollReports /></ProtectedRoute>} />
      <Route path="/task-assignments" element={<ProtectedRoute><TaskAssignments /></ProtectedRoute>} />
      <Route path="/attendance" element={<ProtectedRoute><AttendanceTracking /></ProtectedRoute>} />
      <Route path="/documentation" element={<ProtectedRoute><Documentation /></ProtectedRoute>} />
      <Route path="/audit-logs" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
      <Route path="/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
      <Route path="/packaging-rules" element={<ProtectedRoute><PackagingRules /></ProtectedRoute>} />

      <Route path="/growth/:section?" element={<ProtectedRoute><Growth /></ProtectedRoute>} />

      {/* Employee Portal Routes */}
      <Route path="/my-dashboard" element={<ProtectedRoute><MyDashboard /></ProtectedRoute>} />
      <Route path="/my-tasks" element={<ProtectedRoute><MyTasks /></ProtectedRoute>} />
      <Route path="/my-profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
      <Route path="/task-categories" element={<ProtectedRoute><TaskCategories /></ProtectedRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingManager />
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
